import React from 'react';
import { NotebookFolder } from '../../pages/NotebookPage';

interface FolderSidebarProps {
  folders: NotebookFolder[];
  selectedFolder: NotebookFolder | null;
  onSelectFolder: (folder: NotebookFolder) => void;
  onCreatePage: (folderId: string) => void;
  isCreatingFolder: boolean;
  newFolderName: string;
  onNewFolderNameChange: (name: string) => void;
  onCreateFolder: () => void;
  onStartCreatingFolder: () => void;
  onCancelCreatingFolder: () => void;
}

const FolderSidebar: React.FC<FolderSidebarProps> = ({
  folders,
  selectedFolder,
  onSelectFolder,
  onCreatePage,
  isCreatingFolder,
  newFolderName,
  onNewFolderNameChange,
  onCreateFolder,
  onStartCreatingFolder,
  onCancelCreatingFolder,
}) => {
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onCreateFolder();
    } else if (e.key === 'Escape') {
      onCancelCreatingFolder();
    }
  };

  return (
    <div className="flex flex-col h-full bg-white border-r border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <a
              href="/"
              className="p-1 mr-2 text-gray-400 hover:text-gray-600 transition-colors"
              title="Go to Homepage"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </a>
            <h2 className="text-lg font-semibold text-gray-900">NoteMann</h2>
          </div>
          <button
            onClick={onStartCreatingFolder}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            title="Create New Folder"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>
      </div>

      {/* Folders List */}
      <div className="flex-1 overflow-y-auto p-2">
        {/* New Folder Input */}
        {isCreatingFolder && (
          <div className="mb-2 p-2 bg-gray-50 rounded-lg border-2 border-blue-200">
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => onNewFolderNameChange(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Folder name..."
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            />
            <div className="flex justify-end space-x-1 mt-2">
              <button
                onClick={onCancelCreatingFolder}
                className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onCreateFolder}
                disabled={!newFolderName.trim()}
                className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Create
              </button>
            </div>
          </div>
        )}

        {/* Folder Items */}
        {folders.map((folder) => (
          <div
            key={folder.id}
            className={`group mb-1 p-2 rounded-lg cursor-pointer transition-colors ${
              selectedFolder?.id === folder.id
                ? 'bg-blue-50 border border-blue-200'
                : 'hover:bg-gray-50'
            }`}
            onClick={() => onSelectFolder(folder)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: folder.color }}
                />
                <span className="text-sm font-medium text-gray-900 truncate">
                  {folder.name}
                </span>
                <span className="text-xs text-gray-500 flex-shrink-0">
                  {folder.pageCount}
                </span>
              </div>
              
              {/* Add Page Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onCreatePage(folder.id);
                }}
                className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded transition-all"
                title="Add new page"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
            
            {folder.description && (
              <p className="text-xs text-gray-500 mt-1 truncate">
                {folder.description}
              </p>
            )}
          </div>
        ))}

        {/* Empty State */}
        {folders.length === 0 && !isCreatingFolder && (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">📁</div>
            <p className="text-sm">No folders yet</p>
            <p className="text-xs text-gray-400 mt-1">
              Click the + button to create your first folder
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FolderSidebar; 